import {
  useEffect,
  useState,
  type MouseEvent,
  type ReactNode,
} from 'react';
import {
  alpha,
  Avatar,
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  AccountCircleRounded,
  CloseRounded,
  ExpandLess,
  ExpandMore,
  MenuRounded,
} from '@mui/icons-material';

import {
  useAuth,
} from '../../../context/AuthContext';


export type StacShellView =
  | 'today'
  | 'dedication'
  | 'supervision'
  | 'team'
  | 'projects'
  | 'organization';


export interface StacShellNavItem {
  value: StacShellView;
  label: string;
  title: string;
  description: string;
  icon: ReactNode;
}


export interface StacShellNavGroup {
  label: string;
  items: StacShellNavItem[];
}


interface StacShellProps {
  view: StacShellView;
  groups: StacShellNavGroup[];
  displayName: string;
  contextName: string;
  onViewChange: (
    view: StacShellView,
  ) => void;
  children: ReactNode;
}


interface StacNavItemProps {
  label: string;
  selected: boolean;
  nested?: boolean;
  onClick: () => void;
}


function StacNavItem({
  label,
  selected,
  nested = false,
  onClick,
}: StacNavItemProps) {
  return (
    <ListItemButton
      onClick={onClick}
      sx={{
        minHeight:
          nested ? 40 : 46,
        borderRadius: 2.5,
        px:
          nested ? 2.25 : 2,
        pl:
          nested ? 4 : 2,
        mb: 0.5,
        bgcolor:
          selected
            ? alpha(
                '#38bdf8',
                0.12,
              )
            : 'transparent',
        border:
          `1px solid ${
            selected
              ? alpha(
                  '#38bdf8',
                  0.18,
                )
              : 'transparent'
          }`,
        color:
          selected
            ? '#f8fafc'
            : '#94a3b8',
        transition:
          'all 0.18s ease',
        '&:hover': {
          bgcolor:
            selected
              ? alpha(
                  '#38bdf8',
                  0.14,
                )
              : alpha(
                  '#ffffff',
                  0.03,
                ),
          color: '#e2e8f0',
        },
      }}
    >
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          fontSize:
            nested
              ? '0.93rem'
              : '0.98rem',
          fontWeight:
            selected
              ? 800
              : 700,
          lineHeight: 1.2,
        }}
      />
    </ListItemButton>
  );
}


interface StacNavGroupProps {
  title: string;
  expanded: boolean;
  active: boolean;
  onToggle: () => void;
  children: ReactNode;
}


function StacNavGroup({
  title,
  expanded,
  active,
  onToggle,
  children,
}: StacNavGroupProps) {
  return (
    <Box sx={{ mb: 0.75 }}>
      <ListItemButton
        onClick={onToggle}
        sx={{
          minHeight: 46,
          borderRadius: 2.5,
          px: 2,
          mb: 0.5,
          bgcolor:
            active
              ? alpha(
                  '#ffffff',
                  0.03,
                )
              : 'transparent',
          color:
            active
              ? '#f8fafc'
              : '#cbd5e1',
          transition:
            'all 0.18s ease',
          '&:hover': {
            bgcolor:
              alpha(
                '#ffffff',
                0.03,
              ),
          },
        }}
      >
        <ListItemText
          primary={title}
          primaryTypographyProps={{
            fontSize:
              '0.98rem',
            fontWeight: 800,
            lineHeight: 1.2,
          }}
        />

        {expanded ? (
          <ExpandLess
            sx={{
              color:
                active
                  ? '#e2e8f0'
                  : '#64748b',
            }}
          />
        ) : (
          <ExpandMore
            sx={{
              color:
                active
                  ? '#e2e8f0'
                  : '#64748b',
            }}
          />
        )}
      </ListItemButton>

      <Collapse
        in={expanded}
        timeout="auto"
        unmountOnExit
      >
        <Box sx={{ pt: 0.25 }}>
          {children}
        </Box>
      </Collapse>
    </Box>
  );
}


interface StacDesktopRailButtonProps {
  label: string;
  icon: ReactNode;
  selected: boolean;
  onClick?: () => void;
}


function StacDesktopRailButton({
  label,
  icon,
  selected,
  onClick,
}: StacDesktopRailButtonProps) {
  return (
    <IconButton
      title={label}
      aria-label={label}
      onClick={onClick}
      sx={{
        width: 46,
        height: 46,
        mb: 0.5,
        p: 0,
        borderRadius: 2.5,
        color:
          selected
            ? '#f8fafc'
            : '#94a3b8',
        bgcolor:
          selected
            ? alpha(
                '#38bdf8',
                0.16,
              )
            : 'transparent',
        border:
          `1px solid ${
            selected
              ? alpha(
                  '#38bdf8',
                  0.24,
                )
              : 'transparent'
          }`,
        transition:
          'all 0.18s ease',
        '& .MuiSvgIcon-root': {
          fontSize: 22,
        },
        '&:hover': {
          color: '#e2e8f0',
          bgcolor:
            selected
              ? alpha(
                  '#38bdf8',
                  0.18,
                )
              : alpha(
                  '#ffffff',
                  0.05,
                ),
          borderColor:
            alpha(
              '#38bdf8',
              0.2,
            ),
        },
      }}
    >
      {icon}
    </IconButton>
  );
}


export default function StacShell({
  view,
  groups,
  displayName,
  contextName,
  onViewChange,
  children,
}: StacShellProps) {
  const {
    signOut,
  } = useAuth();

  const shouldUseCompactSidebar =
    useMediaQuery(
      '(max-width:1199.95px), '
      + '(max-width:1535.95px) '
      + 'and (any-pointer:coarse)',
    );

  const [
    mobileSidebarOpen,
    setMobileSidebarOpen,
  ] = useState(false);

  const [
    desktopSidebarOpen,
    setDesktopSidebarOpen,
  ] = useState(false);

  const [
    desktopSidebarContentVisible,
    setDesktopSidebarContentVisible,
  ] = useState(false);

  const [
    userMenuAnchorEl,
    setUserMenuAnchorEl,
  ] = useState<HTMLElement | null>(
    null,
  );

  const [
    logoutDialogOpen,
    setLogoutDialogOpen,
  ] = useState(false);

  const [
    expandedGroups,
    setExpandedGroups,
  ] = useState<
    Record<string, boolean>
  >({});

  const isDesktopSidebarExpanded =
    !shouldUseCompactSidebar
    && (
      desktopSidebarOpen
      || Boolean(
        userMenuAnchorEl
      )
    );

  const isDesktopSidebarContentVisible =
    shouldUseCompactSidebar
    || desktopSidebarContentVisible;

  const activeGroup =
    groups.find(
      (group) =>
        group.items.some(
          (item) =>
            item.value
            === view,
        ),
    );

  useEffect(() => {
    if (
      activeGroup
      && !expandedGroups[
        activeGroup.label
      ]
    ) {
      setExpandedGroups(
        (previous) => ({
          ...previous,
          [activeGroup.label]:
            true,
        }),
      );
    }
  }, [
    activeGroup,
    expandedGroups,
  ]);

  useEffect(() => {
    if (
      shouldUseCompactSidebar
    ) {
      setDesktopSidebarContentVisible(
        false,
      );
      return;
    }

    if (
      !isDesktopSidebarExpanded
    ) {
      setDesktopSidebarContentVisible(
        false,
      );
      return;
    }

    const timeoutId =
      window.setTimeout(
        () => {
          setDesktopSidebarContentVisible(
            true,
          );
        },
        90,
      );

    return () => {
      window.clearTimeout(
        timeoutId,
      );
    };
  }, [
    isDesktopSidebarExpanded,
    shouldUseCompactSidebar,
  ]);

  const setCurrentView = (
    nextView: StacShellView,
  ) => {
    onViewChange(nextView);
    setMobileSidebarOpen(
      false,
    );
  };

  const toggleGroup = (
    label: string,
  ) => {
    setExpandedGroups(
      (previous) => ({
        ...previous,
        [label]:
          !previous[label],
      }),
    );
  };

  const handleOpenUserMenu = (
    event:
      MouseEvent<HTMLElement>,
  ) => {
    setUserMenuAnchorEl(
      event.currentTarget,
    );
  };

  const handleCloseUserMenu =
    () => {
      setUserMenuAnchorEl(
        null,
      );
    };

  const handleRequestLogout =
    () => {
      handleCloseUserMenu();
      setLogoutDialogOpen(
        true,
      );
    };

  const handleCancelLogout =
    () => {
      setLogoutDialogOpen(
        false,
      );
    };

  const handleConfirmLogout =
    async () => {
      try {
        setLogoutDialogOpen(
          false,
        );

        await signOut();

        window.location.href =
          '/stac';
      } catch (error) {
        console.error(
          'STAC logout error',
          error,
        );
      }
    };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#081120',
        color: '#f8fafc',
        pl:
          shouldUseCompactSidebar
            ? {
                xs: 2,
                md: 3,
                xl: 4,
              }
            : 0,
        pr: {
          xs: 2,
          md: 3,
          xl: 4,
        },
        py: {
          xs: 2.5,
          md: 3,
        },
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns:
            shouldUseCompactSidebar
              ? '1fr'
              : '72px minmax(0, 1fr)',
          gap:
            shouldUseCompactSidebar
              ? 3
              : 2.5,
          alignItems: 'start',
        }}
      >
        <Button
          variant="outlined"
          startIcon={
            <MenuRounded />
          }
          onClick={() =>
            setMobileSidebarOpen(
              true,
            )
          }
          sx={{
            display:
              shouldUseCompactSidebar
                ? 'inline-flex'
                : 'none',
            justifySelf: 'start',
            color: '#bae6fd',
            borderColor:
              alpha(
                '#38bdf8',
                0.28,
              ),
            fontWeight: 900,
            textTransform: 'none',
          }}
        >
          Menú
        </Button>

        <Box
          onClick={() =>
            setMobileSidebarOpen(
              false,
            )
          }
          sx={{
            display:
              shouldUseCompactSidebar
              && mobileSidebarOpen
                ? 'block'
                : 'none',
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            bgcolor:
              'rgba(2,6,23,0.68)',
            backdropFilter:
              'blur(3px)',
          }}
        />

        <Box
          onMouseEnter={() => {
            if (
              !shouldUseCompactSidebar
            ) {
              setDesktopSidebarOpen(
                true,
              );
            }
          }}
          onMouseLeave={() => {
            if (
              !shouldUseCompactSidebar
              && !userMenuAnchorEl
            ) {
              setDesktopSidebarOpen(
                false,
              );
            }
          }}
          sx={{
            border:
              `1px solid ${
                alpha(
                  '#94a3b8',
                  0.16,
                )
              }`,
            bgcolor:
              'rgba(8,18,36,0.97)',
            backdropFilter:
              'blur(12px)',
            borderRadius:
              shouldUseCompactSidebar
                ? 0
                : 4,
            p:
              shouldUseCompactSidebar
                ? 1.5
                : isDesktopSidebarExpanded
                  ? 1.5
                  : 1,
            minHeight:
              shouldUseCompactSidebar
                ? '100vh'
                : 'calc(100vh - 48px)',
            display:
              shouldUseCompactSidebar
                ? mobileSidebarOpen
                  ? 'flex'
                  : 'none'
                : 'flex',
            flexDirection: 'column',
            position:
              shouldUseCompactSidebar
                ? 'fixed'
                : 'sticky',
            top:
              shouldUseCompactSidebar
                ? 0
                : 24,
            left:
              shouldUseCompactSidebar
                ? 0
                : 'auto',
            bottom:
              shouldUseCompactSidebar
                ? 0
                : 'auto',
            width:
              shouldUseCompactSidebar
                ? 'min(88vw, 340px)'
                : isDesktopSidebarExpanded
                  ? 300
                  : 72,
            maxHeight:
              shouldUseCompactSidebar
                ? '100vh'
                : 'none',
            overflowX:
              shouldUseCompactSidebar
                ? 'visible'
                : 'hidden',
            overflowY:
              shouldUseCompactSidebar
                ? 'auto'
                : 'visible',
            transition:
              shouldUseCompactSidebar
                ? undefined
                : (
                  'width 0.18s ease, '
                  + 'padding 0.18s ease, '
                  + 'box-shadow 0.16s ease'
                ),
            boxShadow:
              !shouldUseCompactSidebar
              && isDesktopSidebarExpanded
                ? (
                  '0 24px 60px '
                  + 'rgba(2,6,23,0.46)'
                )
                : 'none',
            zIndex:
              shouldUseCompactSidebar
                ? 1301
                : 1200,
          }}
        >
          <Box
            sx={{
              display:
                !shouldUseCompactSidebar
                && !isDesktopSidebarExpanded
                  ? 'flex'
                  : 'none',
              flexDirection: 'column',
              alignItems: 'center',
              minHeight:
                'calc(100vh - 80px)',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent:
                  'center',
                width: '100%',
                pt: 0.75,
                pb: 2,
                borderBottom:
                  `1px solid ${
                    alpha(
                      '#94a3b8',
                      0.12,
                    )
                  }`,
              }}
            >
              <Box
                component="img"
                src="/logo.png"
                alt="PROA / STAC"
                sx={{
                  height: 30,
                  width: 'auto',
                  objectFit:
                    'contain',
                  flexShrink: 0,
                  filter:
                    'drop-shadow('
                    + '0 0 8px '
                    + 'rgba(56,189,248,0.3)'
                    + ')',
                }}
              />
            </Box>

            <Box
              sx={{
                pt: 2,
                flex: 1,
                width: '100%',
                minWidth: 0,
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  px: 1,
                  pb: 1.25,
                  visibility:
                    'hidden',
                  pointerEvents:
                    'none',
                  userSelect:
                    'none',
                }}
              >
                Navegación
              </Typography>

              <List
                disablePadding
                sx={{
                  display: 'flex',
                  flexDirection:
                    'column',
                  alignItems:
                    'center',
                  width: '100%',
                }}
              >
                {groups.map(
                  (group) => {
                    const selected =
                      group.items.some(
                        (item) =>
                          item.value
                          === view,
                      );

                    return (
                      <StacDesktopRailButton
                        key={
                          group.label
                        }
                        label={
                          group.label
                        }
                        icon={
                          group.items[
                            0
                          ]?.icon
                        }
                        selected={
                          selected
                        }
                        onClick={() => {
                          setExpandedGroups(
                            (previous) => ({
                              ...previous,
                              [group.label]:
                                true,
                            }),
                          );
                        }}
                      />
                    );
                  },
                )}
              </List>
            </Box>

            <Box
              sx={{
                mt: 2,
                pt: 1.5,
                borderTop:
                  `1px solid ${
                    alpha(
                      '#94a3b8',
                      0.12,
                    )
                  }`,
                width: '100%',
                display: 'flex',
                justifyContent:
                  'center',
              }}
            >
              <IconButton
                title={
                  displayName
                }
                aria-label="Usuario"
                onClick={
                  handleOpenUserMenu
                }
                sx={{
                  width: 46,
                  height: 46,
                  p: 0,
                  borderRadius: 2.5,
                  bgcolor:
                    alpha(
                      '#ffffff',
                      0.025,
                    ),
                  border:
                    `1px solid ${
                      alpha(
                        '#94a3b8',
                        0.1,
                      )
                    }`,
                  transition:
                    'all 0.18s ease',
                  '&:hover': {
                    bgcolor:
                      alpha(
                        '#ffffff',
                        0.05,
                      ),
                    borderColor:
                      alpha(
                        '#38bdf8',
                        0.22,
                      ),
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 34,
                    height: 34,
                    bgcolor:
                      alpha(
                        '#38bdf8',
                        0.1,
                      ),
                    color:
                      '#38bdf8',
                    border:
                      `1px solid ${
                        alpha(
                          '#38bdf8',
                          0.3,
                        )
                      }`,
                  }}
                >
                  <AccountCircleRounded
                    fontSize="medium"
                  />
                </Avatar>
              </IconButton>
            </Box>
          </Box>

          <Box
            sx={{
              display:
                shouldUseCompactSidebar
                || isDesktopSidebarExpanded
                  ? 'block'
                  : 'none',
              opacity:
                isDesktopSidebarContentVisible
                  ? 1
                  : 0,
              transform:
                isDesktopSidebarContentVisible
                  ? 'translateX(0)'
                  : 'translateX(-4px)',
              transition:
                shouldUseCompactSidebar
                  ? undefined
                  : (
                    'opacity 0.14s ease, '
                    + 'transform 0.14s ease'
                  ),
              pointerEvents:
                isDesktopSidebarContentVisible
                  ? 'auto'
                  : 'none',
              px: 1.25,
              pt: 0.5,
              pb: 2.25,
              borderBottom:
                `1px solid ${
                  alpha(
                    '#94a3b8',
                    0.12,
                  )
                }`,
            }}
          >
            <Stack spacing={0.75}>
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="center"
              >
                <Box
                  component="img"
                  src="/logo.png"
                  alt="PROA STAC"
                  sx={{
                    height: 30,
                    width: 'auto',
                    objectFit:
                      'contain',
                    flexShrink: 0,
                    filter:
                      'drop-shadow('
                      + '0 0 8px '
                      + 'rgba(56,189,248,0.3)'
                      + ')',
                  }}
                />

                <Typography
                  sx={{
                    color:
                      '#f8fafc',
                    fontWeight: 900,
                    letterSpacing:
                      '-0.6px',
                    fontSize:
                      '1.18rem',
                    lineHeight: 1,
                  }}
                >
                  PROA STAC
                </Typography>

                <IconButton
                  aria-label="Cerrar menú"
                  onClick={() =>
                    setMobileSidebarOpen(
                      false,
                    )
                  }
                  sx={{
                    display:
                      shouldUseCompactSidebar
                        ? 'inline-flex'
                        : 'none',
                    ml: 'auto',
                    color:
                      '#94a3b8',
                  }}
                >
                  <CloseRounded />
                </IconButton>
              </Stack>

              <Typography
                sx={{
                  pl: 5.25,
                  color: '#64748b',
                  fontWeight: 800,
                  letterSpacing: 0.8,
                  fontSize: '0.68rem',
                  lineHeight: 1,
                  textTransform:
                    'uppercase',
                }}
              >
                Trabajo y tiempos
              </Typography>
            </Stack>
          </Box>

          <Box
            sx={{
              display:
                shouldUseCompactSidebar
                || isDesktopSidebarExpanded
                  ? 'block'
                  : 'none',
              opacity:
                isDesktopSidebarContentVisible
                  ? 1
                  : 0,
              transform:
                isDesktopSidebarContentVisible
                  ? 'translateX(0)'
                  : 'translateX(-4px)',
              transition:
                shouldUseCompactSidebar
                  ? undefined
                  : (
                    'opacity 0.14s ease, '
                    + 'transform 0.14s ease'
                  ),
              pointerEvents:
                isDesktopSidebarContentVisible
                  ? 'auto'
                  : 'none',
              pt: 2,
              flex: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                px: 1,
                pb: 1.25,
                color: '#64748b',
                fontWeight: 800,
                letterSpacing: 1.1,
                textTransform:
                  'uppercase',
              }}
            >
              Navegación
            </Typography>

            <List disablePadding>
              {groups.map(
                (group) => {
                  const active =
                    group.items.some(
                      (item) =>
                        item.value
                        === view,
                    );

                  return (
                    <StacNavGroup
                      key={
                        group.label
                      }
                      title={
                        group.label
                      }
                      expanded={
                        expandedGroups[
                          group.label
                        ]
                        === true
                      }
                      active={
                        active
                      }
                      onToggle={() =>
                        toggleGroup(
                          group.label,
                        )
                      }
                    >
                      {group.items.map(
                        (item) => (
                          <StacNavItem
                            key={
                              item.value
                            }
                            label={
                              item.label
                            }
                            nested
                            selected={
                              item.value
                              === view
                            }
                            onClick={() =>
                              setCurrentView(
                                item.value,
                              )
                            }
                          />
                        ),
                      )}
                    </StacNavGroup>
                  );
                },
              )}
            </List>
          </Box>

          <Box
            sx={{
              display:
                shouldUseCompactSidebar
                || isDesktopSidebarExpanded
                  ? 'block'
                  : 'none',
              opacity:
                isDesktopSidebarContentVisible
                  ? 1
                  : 0,
              transform:
                isDesktopSidebarContentVisible
                  ? 'translateX(0)'
                  : 'translateX(-4px)',
              transition:
                shouldUseCompactSidebar
                  ? undefined
                  : (
                    'opacity 0.14s ease, '
                    + 'transform 0.14s ease'
                  ),
              pointerEvents:
                isDesktopSidebarContentVisible
                  ? 'auto'
                  : 'none',
              mt: 2,
              pt: 1.5,
              borderTop:
                `1px solid ${
                  alpha(
                    '#94a3b8',
                    0.12,
                  )
                }`,
            }}
          >
            <ListItemButton
              onClick={
                handleOpenUserMenu
              }
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                px: 1.25,
                py: 1.25,
                borderRadius: 2.5,
                bgcolor:
                  alpha(
                    '#ffffff',
                    0.025,
                  ),
                border:
                  `1px solid ${
                    alpha(
                      '#94a3b8',
                      0.1,
                    )
                  }`,
                transition:
                  'all 0.18s ease',
                '&:hover': {
                  bgcolor:
                    alpha(
                      '#ffffff',
                      0.05,
                    ),
                  border:
                    `1px solid ${
                      alpha(
                        '#38bdf8',
                        0.22,
                      )
                    }`,
                },
              }}
            >
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor:
                    alpha(
                      '#38bdf8',
                      0.1,
                    ),
                  color:
                    '#38bdf8',
                  border:
                    `1px solid ${
                      alpha(
                        '#38bdf8',
                        0.3,
                      )
                    }`,
                }}
              >
                <AccountCircleRounded
                  fontSize="medium"
                />
              </Avatar>

              <Box
                sx={{
                  minWidth: 0,
                  flex: 1,
                }}
              >
                <Typography
                  noWrap
                  sx={{
                    color:
                      '#f8fafc',
                    fontWeight: 850,
                    fontSize:
                      '0.9rem',
                    lineHeight: 1.2,
                  }}
                >
                  {displayName}
                </Typography>

                <Typography
                  noWrap
                  sx={{
                    color:
                      '#64748b',
                    fontWeight: 700,
                    fontSize:
                      '0.76rem',
                    lineHeight: 1.2,
                  }}
                >
                  {contextName}
                </Typography>
              </Box>
            </ListItemButton>
          </Box>
        </Box>

        <Box
          sx={{
            minWidth: 0,
          }}
        >
          {children}
        </Box>
      </Box>

      <Menu
        anchorEl={
          userMenuAnchorEl
        }
        open={
          Boolean(
            userMenuAnchorEl,
          )
        }
        onClose={
          handleCloseUserMenu
        }
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        sx={{
          zIndex:
            shouldUseCompactSidebar
              ? 1402
              : 1300,
        }}
      >
        <MenuItem
          onClick={
            handleRequestLogout
          }
        >
          Cerrar sesión
        </MenuItem>
      </Menu>

      <Dialog
        open={
          logoutDialogOpen
        }
        onClose={
          handleCancelLogout
        }
      >
        <DialogTitle>
          Cerrar sesión
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            ¿Seguro que quieres cerrar
            la sesión de PROA STAC?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={
              handleCancelLogout
            }
          >
            Cancelar
          </Button>

          <Button
            color="error"
            variant="contained"
            onClick={
              handleConfirmLogout
            }
          >
            Cerrar sesión
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
